import { css, Theme } from '@emotion/react';
import { Skeleton, Typography } from '@mui/material';
import { Box, Stack, StackProps } from '@mui/system';
import { Types } from '@scorenco/core';

import { ClubLogo } from '../../components';

import { useCompetitionRankingContext } from './CompetitionRankingContext';

const style = {
  root: (theme: Theme) => css`
    .event-item_team {
      flex: 1;
      line-height: 1.2;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      padding: 0 ${theme.spacing(2)};
      font-size: 0.8rem;
    }
    .bold {
      font-weight: 700;
    }
  `,
};

type TeamNameProps = {
  ranking?: Types.Competitions_Ranking_Detail;
  team?: any;
  showShortName?: boolean;
  clubId?: number;
};

const TeamName = ({ ranking, clubId, showShortName }: TeamNameProps) => {
  const { highlightTeams } = useCompetitionRankingContext();
  const isHighlight =
    ranking?.team_in_season_id &&
    highlightTeams?.includes(ranking.team_in_season_id);

  return (
    <Box css={style.root}>
      <Typography
        className={'team-name'}
        sx={[
          {
            display: '-webkit-box',
            textAlign: 'left',
            flex: 1,
            lineHeight: 1.2,
            overflow: 'hidden',
            WebkitLineClamp: 2,
            textOverflow: 'ellipsis',
            '-webkit-box-orient': 'vertical',
          },
          isHighlight
            ? {
                fontWeight: 'bold',
              }
            : {
                fontWeight: 'normal',
              },
        ]}
        variant="body2"
      >
        {(ranking?.club_id === clubId && showShortName === true
          ? ranking?.name_in_club
          : ranking?.name_in_competition) || <Skeleton />}
      </Typography>
    </Box>
  );
};

export type CompetitionRankingRowProps = Omit<StackProps, 'css'> & {
  ranking?: Types.Competitions_Ranking_Detail;
  condensed?: boolean;
  showShortName?: boolean;
  clubId?: number;
  // currentTeam?: number;
  // coloredTeams?: number[];
  // competitionId?: number;
};

export const CompetitionRankingRow = ({
  ranking,
  condensed = false,
  showShortName = false,
  clubId,
  // currentTeam,
  // coloredTeams = [],
  sx,
  ...rest
}: CompetitionRankingRowProps) => {
  const { highlightTeams, infoWidth, teamWidth } =
    useCompetitionRankingContext();
  const isHighlight =
    ranking?.team_in_season_id &&
    highlightTeams?.includes(ranking.team_in_season_id);

  return (
    <Stack
      direction="row"
      className="competition-ranking-row"
      sx={
        [
          {
            minHeight: '3rem',
            alignItems: 'center',
            justifyContent: 'start',
            gap: 1,
            borderLeft: '0.25rem solid',
            ...sx,
          },
          isHighlight
            ? {
                borderColor: 'primary.main',
              }
            : {
                borderColor: 'transparent',
              },
        ] as any
      }
      {...rest}
    >
      <Stack
        direction="row"
        className="team-info"
        sx={{
          alignItems: 'center',
          justifyContent: 'start',
          gap: 1,
          flex: `1 0 ${teamWidth}rem`,
        }}
      >
        <Typography
          className="team-rank"
          sx={[
            {
              flex: '0 0 1.75rem',
              width: '1.75rem',
              textAlign: 'right',
              mr: 1,
            },
            isHighlight
              ? {
                  fontWeight: 'bold',
                }
              : {
                  fontWeight: 'normal',
                },
          ]}
          variant="body2"
        >
          {ranking?.rank as number | undefined}
        </Typography>

        <ClubLogo
          className="team-logo"
          css={css`
            flex: 0 0 1.25rem;
            width: 1.25rem;
            height: 1.75rem;
          `}
          width={32}
          height={32}
          logo={ranking?.logo}
        />

        {!condensed && (
          <TeamName
            ranking={ranking}
            showShortName={showShortName}
            clubId={clubId}
          />
        )}
      </Stack>
      <Box
        className="info-box"
        sx={{
          flex: `0 1 ${infoWidth}rem`,
        }}
      />
    </Stack>
  );
};
